import { motion } from "framer-motion";
import { Users, Calendar, FileText, Award } from "lucide-react";
import SEO from "@/components/SEO";

const Gestion = () => {
  const members = [
    {
      title: "Présidence",
      description: "Un représentant de la Présidence de la République",
      icon: <Users className="w-8 h-8" />,
    },
    {
      title: "Ministères",
      description: [
        "Un représentant de la Primature",
        "Un représentant du Ministère en charge des Finances et du Budget",
        "Un représentant du Ministère en charge des Femmes",
        "Un représentant du Ministère en charge du Commerce et des Petites et Moyennes entreprises",
        "Un représentant du Ministère en charge de l'Artisanat et de la Transformation du Secteur informel",
      ],
      icon: <FileText className="w-8 h-8" />,
    },
    {
      title: "Organisations",
      description: [
        "Un représentant de la Délégation à l'Entreprenariat rapide des Femmes et des Jeunes (DER/E1)",
        "Un représentant des Chambres de métiers",
        "Un représentant des Organisations professionnelles Artisanales (OPA)",
      ],
      icon: <Award className="w-8 h-8" />,
    },
  ];

  return (
    <>
      <SEO
        title="Gestion"
        description="Découvrez la structure de gestion du Projet Mobilier National, le Comité de Pilotage et son rôle dans la modernisation de l'artisanat sénégalais."
        keywords="PMN, Gestion, Comité de Pilotage, Artisanat, Sénégal, Organisation, Structure, Direction"
        ogImage="/logo/logo.png"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-[#1E3D1A] py-32">
          <div className="absolute inset-0 bg-[url('/back.webp')] opacity-60" />
          <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-wider">
                COMITÉ DE PILOTAGE (COPIL)
              </h1>
              <p className="text-xl text-white/90">
                Structure de gouvernance du Projet Mobilier National
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="bg-white rounded-2xl p-8 shadow-lg mb-12"
              >
                <p className="text-lg text-gray-700 leading-relaxed">
                  Le Comité de Pilotage de la coordination du PMN est présidé par le représentant de la Présidence de la République.
                </p>
              </motion.div>

              {/* Members Section */}
              <div className="space-y-12">
                {members.map((group, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-8 shadow-lg"
                  >
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="bg-[#1E3D1A] p-3 rounded-lg text-white">
                        {group.icon}
                      </div>
                      <h2 className="text-2xl font-bold text-[#1E3D1A]">
                        {group.title}
                      </h2>
                    </div>
                    {Array.isArray(group.description) ? (
                      <ul className="space-y-4">
                        {group.description.map((item, i) => (
                          <li key={i} className="flex items-start space-x-3">
                            <div className="w-2 h-2 rounded-full bg-[#1E3D1A] mt-2" />
                            <p className="text-gray-600 leading-relaxed">{item}</p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600 leading-relaxed">
                        {group.description}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Additional Information */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-12 space-y-8"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-[#1E3D1A] p-3 rounded-lg text-white">
                      <Calendar className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1E3D1A]">
                      Réunions du Comité
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    Le Comité de pilotage se réunit au moins trois (03) fois par an et à chaque fois que de besoin, sur convocation de son Président. Le Secrétariat du Comité de pilotage est assuré par le Coordonnateur du Projet Mobilier national.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1E3D1A] mb-4">
                    Expertise Complémentaire
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Le Comité de pilotage peut s'adjoindre, avec voix consultative, toute personne dont les compétences et l'expertise en matière de mobilier national sont reconnues.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1E3D1A] mb-4">
                    Indemnités de Session
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Les membres du Comité de pilotage perçoivent, à l'occasion des réunions du Comité de pilotage, une indemnité de session fixée par décret n°2024-674 du 1er Mars 2024 fixant l'indemnité de session des membres du Comité de pilotage du Projet Mobilier national.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Gestion;
